export * from "./types.js";
export * from "./copy.js";
export {
  overpaymentVsReference,
  loyaltyTaxEstimate,
  assertNoAffiliateOnResult,
  AffiliateOnResultError,
} from "./calculate.js";
export { dmo8_2026_27 } from "./dmo8-2026-27.js";

import type { EnergyOverpaymentRuleSet } from "./types.js";
import { dmo8_2026_27 } from "./dmo8-2026-27.js";

/**
 * Registry of energy-overpayment rulesets keyed by DMO determination period. The
 * DMO reprices annually effective 1 July; each determination (DMO 7, DMO 8, …)
 * gets its own dated file. Historical periods stay registered so a backdated
 * "X% over the reference" comparison resolves against the determination in force
 * on the assessed date — never mutate a shipped table, append a new dated one.
 *
 * Only the DMO 8 (2026-27) period is registered today; prior/future
 * determinations can be added as `dmoN-YYYY-YY.ts` files without touching shipped
 * ones.
 */
export const ENERGY_OVERPAYMENT_RULESETS: Record<string, EnergyOverpaymentRuleSet> =
  {
    "2026-27": dmo8_2026_27,
  };

/** The rulesets, newest first — for date resolution. */
const RULESETS_DESC: readonly EnergyOverpaymentRuleSet[] = Object.values(
  ENERGY_OVERPAYMENT_RULESETS,
).sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1));

/**
 * Resolve the DMO ruleset in force on an ISO date (YYYY-MM-DD). Pure string
 * comparison — ISO dates sort lexically. Picks the most recent determination
 * whose `[effectiveFrom, effectiveTo]` window contains the date. Throws if the
 * date predates the earliest registered determination.
 */
export function energyOverpaymentRulesForDate(
  isoDate: string,
): EnergyOverpaymentRuleSet {
  for (const set of RULESETS_DESC) {
    const afterStart = isoDate >= set.effectiveFrom;
    const beforeEnd = isoDate <= set.effectiveTo;
    if (afterStart && beforeEnd) return set;
  }
  throw new Error(`no DMO ruleset registered for date ${isoDate}`);
}

/** The current (most recent) DMO ruleset — default for live "now" comparisons. */
export const ENERGY_OVERPAYMENT_CURRENT_RULESET: EnergyOverpaymentRuleSet =
  dmo8_2026_27;

/** The current DMO determination period key. */
export const ENERGY_OVERPAYMENT_CURRENT_PERIOD = "2026-27";
